import { useMemo } from 'react';
import { AgentDot } from '../../../AgentDot';
import { useTranslation } from '../../../../lib/i18n';
import { formatMessageTime } from '../message-utils';
import { AssistantMarkdownContent } from '../MessageRow/AssistantMarkdownContent';
import { Typography } from '../../ui';
import { stripLeadingRule } from './strip-leading-rule';
import { BriefOpenDecisions } from './BriefOpenDecisions';
import styles from './MeetingSummaryCard.module.css';

/**
 * The brief round-table's wrap-up, rendered in place of a normal assistant bubble for the turn
 * carrying `metadata.agentMeetingSummary` (see `readMeetingSummaryFlag`).
 */
export function MeetingSummaryCard({
  content,
  createdDate,
  agentIds,
  conversationId,
}: {
  content: string;
  createdDate?: string;
  agentIds: string[];
  /** The brief's own conversation — the questions it raised hang off it. Absent renders no block. */
  conversationId?: string;
}) {
  const { t, i18n } = useTranslation();

  const weekday = useMemo(() => {
    if (!createdDate) return undefined;
    const date = new Date(createdDate);
    if (Number.isNaN(date.getTime())) return undefined;
    try {
      return date.toLocaleDateString(i18n.language || undefined, { weekday: 'long' });
    } catch {
      return undefined;
    }
  }, [createdDate, i18n.language]);
  const time = formatMessageTime(createdDate);

  // The cluster caps at four marks (matching native); earlier speakers sit on top of later ones.
  const cluster = agentIds.slice(0, 4);
  const count = agentIds.length;
  const metLabel = t(`brief.summaryAgentsMet.${count === 1 ? 'one' : 'other'}`, { count });

  return (
    <div className={styles.card}>
      <Typography variant="h2" component="h3" color="strong" className={styles.title}>
        {t('brief.summaryTitle')}
      </Typography>
      <div className={styles.subtitle}>
        {(weekday || time) && (
          <div className={styles.when}>
            {weekday && <span>{weekday}</span>}
            {weekday && time && <span aria-hidden className={styles.dot} />}
            {time && <span>{time}</span>}
          </div>
        )}
        {count > 0 && (
          <div className={styles.met}>
            <div className={styles.avatars}>
              {cluster.map((id, i) => (
                <span key={id} className={styles.avatar} style={{ zIndex: cluster.length - i }}>
                  <AgentDot id={id} size={16} />
                </span>
              ))}
            </div>
            <span className={styles.metLabel}>{metLabel}</span>
          </div>
        )}
      </div>
      <div className={styles.divider} />
      <div className={styles.body}>
        {/* The card already draws the divider above; drop the summary's own leading `---` so the two
            hairlines don't stack into a double border. */}
        <AssistantMarkdownContent displayed={stripLeadingRule(content)} isCursorOn={false} />
      </div>
      {/* Where the meeting's own questions get answered. */}
      <BriefOpenDecisions conversationId={conversationId} />
    </div>
  );
}
